import { useState, useMemo, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, useParams, Navigate } from 'react-router-dom';
import { Box, CssBaseline, ThemeProvider, Typography } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Customer from './pages/Customer';
import Users from './pages/Users';
import Jobs from './pages/job/Jobs';
import JobDetail from './pages/job/JobDetail';
import Quote from './pages/job/Quote';
import ProductTypePage from './pages/ProductType';
import ProductDetailStatusPage from './pages/ProductDetailStatus';
import TypeOfWorkPage from './pages/TypeOfWork';
import ProductPage from './pages/job/Product';
import MaterialStatusDashboard from './pages/MaterialStatusDashboard';
import BarcodeScan from './pages/BarcodeScan';
import WorkingLog from './pages/WorkingLog';
import Schedule from './pages/Schedule';
import AttendanceSheet from './pages/AttendanceSheet';
import MapPage from './pages/Map';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import Integrations from './pages/Integrations';
import IncomeDashboard from './pages/IncomeDashboard';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import OfflineIndicator from './components/OfflineIndicator';
import UpdateAvailableDialog from './components/UpdateAvailableDialog';
import { createAppTheme } from './theme';
import { clearAuth, setSessionActive } from './utils/auth';
import { apiRequest, login, signup, logout } from './utils/api';
import { NotificationProvider } from './context/NotificationContext';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import type { Job, User } from './types';

const drawerWidth = 240;
const drawerMinimizedWidth = 64;

// Theme mode storage key
const THEME_MODE_KEY = 'glasska-theme-mode';

// Helper function to get theme mode from localStorage
const getStoredThemeMode = (): 'light' | 'dark' => {
  const stored = localStorage.getItem(THEME_MODE_KEY);
  return (stored === 'dark' || stored === 'light') ? stored : 'light';
};

// Helper function to save theme mode to localStorage
const saveThemeMode = (mode: 'light' | 'dark') => {
  localStorage.setItem(THEME_MODE_KEY, mode);
};

// Initial jobs data
const initialJobs: Job[] = [
  
];

// Protected Route Component - redirects Field Workers if route is not allowed
function ProtectedRoute({
  children,
  isFieldWorker,
  allowedForFieldWorker,
  adminOnly,
  isAdmin,
}: {
  children: React.ReactElement;
  isFieldWorker: boolean;
  allowedForFieldWorker: boolean;
  adminOnly?: boolean;
  isAdmin?: boolean;
}) {
  if (isFieldWorker && !allowedForFieldWorker) {
    return <Navigate to="/scan" replace />;
  }
  if (adminOnly && !isAdmin) {
    return <Navigate to="/customer" replace />;
  }
  return children;
}

function AppContent({ onLogout }: { onLogout: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarMinimized, setSidebarMinimized] = useState(false);
  const [mode, setMode] = useState<'light' | 'dark'>(() => getStoredThemeMode());
  const [jobs, setJobs] = useState<Job[]>(initialJobs);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Load current user profile to check role
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const response = await apiRequest('/users/profile/me', { method: 'GET' });
        setCurrentUser(response.data as User);
      } catch (error) {
        console.error('Failed to load current user:', error);
      } finally {
        setLoadingUser(false);
      }
    };
    loadCurrentUser();
  }, []);

  // Detect if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      // Check for touch support and screen width
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth <= 768;
      const isMobileDevice = hasTouch && isSmallScreen;
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check if user is a Field Worker
  const isFieldWorker = currentUser?.role === 'FIELD AND SHOP TEAM';
  const isAdmin = currentUser?.role === 'SYSTEM ADMIN';

  // Allowed routes for Field Workers
  const fieldWorkerRoutes = ['/scan', '/clockin', '/jobs'];

  // Redirect Field Workers away from restricted routes
  useEffect(() => {
    if (!loadingUser && isFieldWorker) {
      const path = location.pathname;
      const isAllowed = fieldWorkerRoutes.some(route => path.startsWith(route));
      if (!isAllowed) {
        navigate('/scan', { replace: true });
      }
    }
  }, [location.pathname, isFieldWorker, loadingUser, navigate, fieldWorkerRoutes]);
  
  // Get current page from URL path
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path.startsWith('/customer')) return 'customers';
    if (path.startsWith('/users')) return 'users';
    if (path.startsWith('/jobs')) return isFieldWorker ? 'findjob' : 'jobs';
    if (path.startsWith('/producttype')) return 'producttype';
    if (path.startsWith('/product-detail-status')) return 'productdetailstatus';
    if (path.startsWith('/type-of-work')) return 'typeofwork';
    if (path.startsWith('/products')) return 'products';
    if (path.startsWith('/scan')) return 'scan';
    if (path.startsWith('/clockin')) return 'clockin';
    if (path.startsWith('/schedule')) return 'schedule';
    if (path.startsWith('/attendance')) return 'attendance';
    if (path.startsWith('/map')) return 'map';
    if (path.startsWith('/material-status-dashboard')) return 'materialstatusdashboard';
    if (path.startsWith('/sales')) return 'sales';
    if (path.startsWith('/traffic')) return 'traffic';
    if (path.startsWith('/integrations')) return 'integrations';
    if (path.startsWith('/income')) return 'income';
    return isFieldWorker ? 'scan' : 'customers';
  };
  
  const currentPage = getCurrentPage();

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarMinimized(!sidebarMinimized);
  };

  const toggleTheme = () => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      saveThemeMode(newMode);
      return newMode;
    });
  };

  const currentDrawerWidth = sidebarMinimized ? drawerMinimizedWidth : drawerWidth;

  const handleMenuClick = (menuItem: string) => {
    // Map menu items to routes
    const routeMap: Record<string, string> = {
      'customers': '/customer',
      'users': '/users',
      'jobs': '/jobs',
      'producttype': '/producttype',
      'productdetailstatus': '/product-detail-status',
      'typeofwork': '/type-of-work',
      'products': '/products',
      'scan': '/scan',
      'clockin': '/clockin',
      'findjob': '/jobs',
      'editjob': '/jobs',
      'schedule': '/schedule',
      'attendance': '/attendance',
      'map': '/map',
      'materialstatusdashboard': '/material-status-dashboard',
      'sales': '/sales',
      'traffic': '/traffic',
      'integrations': '/integrations',
      'income': '/income',
    };
    const route = routeMap[menuItem] || (isFieldWorker ? '/scan' : '/customer');
    navigate(route);
  };

  const handleJobClick = (jobId: number) => {
    navigate(`/jobs/${jobId}`);
  };

  const handleBackToJobs = () => {
    navigate('/jobs');
  };

  const handleBackFromQuote = () => {
    // Get jobId from current URL
    const pathParts = location.pathname.split('/');
    const jobId = pathParts[2];
    if (jobId) {
      navigate(`/jobs/${jobId}`);
    } else {
      navigate('/jobs');
    }
  };

  const handleEditJob = (_jobId: number) => {
    // Navigate back to jobs list and trigger edit
    navigate('/jobs');
    // In a real app, you might want to open the edit dialog here
  };

  const getJobById = (id: number): Job | undefined => {
    return jobs.find((j) => j.id === id);
  };

  return (
    <ThemeProvider theme={theme}>
      <NotificationProvider>
      <CssBaseline />
      <OfflineIndicator />
      <UpdateAvailableDialog />
      <Box sx={{ display: 'flex', bgcolor: 'background.default' }}>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={mode === 'dark' ? 'dark' : 'light'}
        />
        <Header
          drawerWidth={currentDrawerWidth}
          handleDrawerToggle={handleDrawerToggle}
          toggleTheme={toggleTheme}
          onLogout={onLogout}
        />
        <Sidebar
          drawerWidth={currentDrawerWidth}
          minimized={sidebarMinimized}
          onToggleMinimize={handleSidebarToggle}
          mobileOpen={mobileOpen}
          handleDrawerToggle={handleDrawerToggle}
          onMenuClick={handleMenuClick}
          currentPage={currentPage}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { xs: '100%', md: `calc(100% - ${currentDrawerWidth}px)` },
            mt: 8,
            bgcolor: 'background.default',
            transition: 'width 0.3s ease',
          }}
        >
          <Routes>
            <Route path="/" element={
              isMobile ? <BarcodeScan /> : <Navigate to={isFieldWorker ? "/scan" : "/customer"} replace />
            } />
            <Route path="/customer" element={
              <ProtectedRoute isFieldWorker={isFieldWorker} allowedForFieldWorker={false}>
                <Customer />
              </ProtectedRoute>
            } />
            <Route path="/users" element={
              <ProtectedRoute isFieldWorker={isFieldWorker} allowedForFieldWorker={false} adminOnly={true} isAdmin={isAdmin}>
                <Users />
              </ProtectedRoute>
            } />
            <Route path="/jobs" element={
              <ProtectedRoute isFieldWorker={isFieldWorker} allowedForFieldWorker={true}>
                <Jobs onJobClick={handleJobClick} jobs={jobs} setJobs={setJobs} />
              </ProtectedRoute>
            } />
            <Route path="/jobs/:jobId" element={
              <ProtectedRoute isFieldWorker={isFieldWorker} allowedForFieldWorker={true}>
                <JobDetailWrapper onBack={handleBackToJobs} onEdit={handleEditJob} getJobById={getJobById} />
              </ProtectedRoute>
            } />
            <Route path="/jobs/:jobId/quote" element={
              <ProtectedRoute isFieldWorker={isFieldWorker} allowedForFieldWorker={false}>
                <QuoteWrapper onBack={handleBackFromQuote} getJobById={getJobById} />
              </ProtectedRoute>
            } />
            <Route path="/producttype" element={
              <ProtectedRoute isFieldWorker={isFieldWorker} allowedForFieldWorker={false}>
                <ProductTypePage />
              </ProtectedRoute>
            } />
            <Route path="/product-detail-status" element={
              <ProtectedRoute isFieldWorker={isFieldWorker} allowedForFieldWorker={false}>
                <ProductDetailStatusPage />
              </ProtectedRoute>
            } />
            <Route path="/type-of-work" element={
              <ProtectedRoute isFieldWorker={isFieldWorker} allowedForFieldWorker={false}>
                <TypeOfWorkPage />
              </ProtectedRoute>
            } />
            <Route path="/products" element={
              <ProtectedRoute isFieldWorker={isFieldWorker} allowedForFieldWorker={false}>
                <ProductPage />
              </ProtectedRoute>
            } />
            <Route path="/scan" element={<BarcodeScan />} />
            <Route path="/clockin" element={<WorkingLog />} />
            <Route path="/findjob" element={<Navigate to="/jobs" replace />} />
            <Route path="/editjob" element={<Navigate to="/jobs" replace />} />
            <Route path="/schedule" element={
              <ProtectedRoute isFieldWorker={isFieldWorker} allowedForFieldWorker={false}>
                <Schedule />
              </ProtectedRoute>
            } />
            <Route path="/attendance" element={
              <ProtectedRoute isFieldWorker={isFieldWorker} allowedForFieldWorker={false}>
                <AttendanceSheet />
              </ProtectedRoute>
            } />
            <Route path="/map" element={
              <ProtectedRoute isFieldWorker={isFieldWorker} allowedForFieldWorker={false}>
                <MapPage />
              </ProtectedRoute>
            } />
            <Route path="/material-status-dashboard" element={
              <ProtectedRoute isFieldWorker={isFieldWorker} allowedForFieldWorker={false}>
                <MaterialStatusDashboard />
              </ProtectedRoute>
            } />
            <Route path="/sales" element={
              <ProtectedRoute isFieldWorker={isFieldWorker} allowedForFieldWorker={false}>
                <Box>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="overline" color="text.secondary">
                      Reports
                    </Typography>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                      Sales
                    </Typography>
                  </Box>
                  <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                    Sales reports content will be displayed here
                  </Box>
                </Box>
              </ProtectedRoute>
            } />
            <Route path="/traffic" element={
              <ProtectedRoute isFieldWorker={isFieldWorker} allowedForFieldWorker={false}>
                <Box>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="overline" color="text.secondary">
                      Reports
                    </Typography>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                      Traffic
                    </Typography>
                  </Box>
                  <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                    Traffic reports content will be displayed here
                  </Box>
                </Box>
              </ProtectedRoute>
            } />
            <Route path="/integrations" element={
              <ProtectedRoute isFieldWorker={isFieldWorker} allowedForFieldWorker={false}>
                <Integrations />
              </ProtectedRoute>
            } />
            <Route path="/income" element={
              <ProtectedRoute isFieldWorker={isFieldWorker} allowedForFieldWorker={false}>
                <IncomeDashboard />
              </ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to={isFieldWorker ? "/scan" : "/customer"} replace />} />
          </Routes>
          <Box sx={{ mt: 4, pb: 2, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">
              © 2026, Glasska. All Rights Reserved.
            </Typography>
          </Box>
        </Box>
      </Box>
      </NotificationProvider>
    </ThemeProvider>
  );
}

// Wrapper components for routes that need URL parameters
function JobDetailWrapper({ onBack, onEdit, getJobById }: { onBack: () => void; onEdit: (id: number) => void; getJobById: (id: number) => Job | undefined }) {
  const { jobId } = useParams<{ jobId: string }>();
  const id = jobId ? parseInt(jobId, 10) : 0;
  return <JobDetail jobId={id} onBack={onBack} onEdit={onEdit} getJobById={getJobById} />;
}

function QuoteWrapper({ onBack, getJobById }: { onBack: () => void; getJobById: (id: number) => Job | undefined }) {
  const { jobId } = useParams<{ jobId: string }>();
  const id = jobId ? parseInt(jobId, 10) : 0;
  return <Quote jobId={id} onBack={onBack} getJobById={getJobById} />;
}

function App() {
  // Authentication is always false until the startup server check confirms the cookie.
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const resetToken = new URLSearchParams(window.location.search).get('reset_token');
  const [mode] = useState<'light' | 'dark'>(() => getStoredThemeMode());

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  // Check auth status with server on mount and periodically
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Verify session with backend — the httpOnly cookie is sent automatically.
        // If the cookie is absent/expired the server returns 401.
        const { apiRequest } = await import('./utils/api');
        await apiRequest('/users/profile/me', { method: 'GET' });
        setSessionActive(true);
        setIsAuthenticated(true);
      } catch {
        clearAuth();
        setIsAuthenticated(false);
      }
    };

    // Check immediately
    checkAuth();

    // Check every 2 minutes with the server
    const interval = setInterval(() => {
      checkAuth();
    }, 120000);

    return () => clearInterval(interval);
  }, []);

  // Handle login
  const handleLogin = async (email: string, password: string) => {
    try {
      const response = await login(email, password);
      // The server sets an httpOnly auth_token cookie — no token to store client-side.
      // Persist only non-sensitive display metadata.
      localStorage.setItem('userEmail', response.user.email);
      localStorage.setItem('userName', response.user.name || email.split('@')[0]);
      setSessionActive(true);
      setIsAuthenticated(true);
    } catch (error) {
      throw error; // Re-throw to let Login component handle it
    }
  };

  // Handle signup
  const handleSignUp = async (email: string, password: string, name?: string, phone?: string, recaptchaToken?: string) => {
    try {
      const response = await signup(email, password, name, phone, recaptchaToken);
      
      // Don't automatically log in - just return success
      // The SignUp component will handle navigation to login page
      return response;
    } catch (error) {
      throw error; // Re-throw to let SignUp component handle it
    }
  };

  // Handle logout
  const handleLogout = async () => {
    await logout(); // clears httpOnly cookie on server
    clearAuth();
    setIsAuthenticated(false);
  };

  // Show login or signup page if not authenticated
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {resetToken ? (
          <ResetPassword
            token={resetToken}
            onDone={() => {
              // Remove token from URL without reload then go to login
              window.history.replaceState({}, '', window.location.pathname);
              setShowSignUp(false);
              setShowForgotPassword(false);
            }}
          />
        ) : showSignUp ? (
          <SignUp
            onSignUp={handleSignUp}
            onNavigateToLogin={() => setShowSignUp(false)}
          />
        ) : showForgotPassword ? (
          <ForgotPassword onNavigateToLogin={() => setShowForgotPassword(false)} />
        ) : (
          <Login
            onLogin={handleLogin}
            onNavigateToForgotPassword={() => setShowForgotPassword(true)}
          />
        )}
      </ThemeProvider>
    );
  }

  return <AppContent onLogout={handleLogout} />;
}

export default App;
